import React, { useMemo, useEffect, useState, useRef } from 'react';
import './NavigationBar.less';
import BrandIcon from '../BrandIcon/BrandIcon';
import HelpAction from '../HelpAction/HelpAction';
import Stepper from '../Stepper/Stepper';
import {
  getNumberOfChallenges,
  getNumberOfCompletedChallenges,
  isFirstStepsScreen,
  isKYCApp,
  isMVV,
  selectBrandSettings,
  selectUser,
  getNumberOfValidGroups,
  getNumberOfCompletedGroups,
} from 'getaway-data-layer';
import { useSelector } from 'react-redux';
import { navLogo } from '../../../assets/brandImages';
import LanguageSelector from '../LanguageSelector/LanguageSelector';

type Props = {
  title: string;
  description: string;
  showHelp?: boolean;
  showArrowBack?: boolean;
  onClose?: () => void;
  containerStyle?: any;
  customActiveStep?: number;
  showLogo?: boolean;
  showBack?: boolean;
  titleStyles?: any;
  showLanguageSelector?: boolean;
  TitleTestId?: string;
  DescriptionTestId?: string;
  headerStyle?: any;
};

const NavigationBar = ({
  title,
  description = '',
  showHelp,
  showArrowBack,
  onClose,
  containerStyle,
  customActiveStep,
  showLogo,
  showBack,
  titleStyles,
  showLanguageSelector,
  TitleTestId,
  DescriptionTestId,
  headerStyle,
}: Props) => {
  const user = useSelector(selectUser) as any;
  const brandSettings = useSelector(selectBrandSettings);
  const headerRef = useRef(null);
  const separateCreditCheckScreen =
    brandSettings?.kycSettings?.separateCreditCheckScreen;

  const numberOfChallenges = useMemo(() => {
    return getNumberOfChallenges(user, separateCreditCheckScreen);
  }, [user?.version, separateCreditCheckScreen]);

  const activeStep = useMemo(() => {
    return customActiveStep || getNumberOfCompletedChallenges(user);
  }, [user, customActiveStep]);

  const [isHeaderVisible, setIsHeaderVisible] = useState(true);

  const numberOfValidGroups = getNumberOfValidGroups(user);
  const numberOfCompletedGroups = getNumberOfCompletedGroups(user);
  const numberOfIncompletedGroups =
    numberOfValidGroups - numberOfCompletedGroups;

  useEffect(() => {
    const screenBody =
      document?.querySelector('.screen-body') ||
      document?.querySelector('.screen-body-faq');
    const headerHeight = 60;

    const handleScroll = () => {
      const scrollPosition = screenBody?.scrollTop || 0;
      const totalScrollHeight = screenBody?.scrollHeight || 0;
      const viewableAreaHeight = screenBody?.clientHeight || 0;
      const extraScrollableSpace = totalScrollHeight - viewableAreaHeight;
      if (scrollPosition === 0) {
        setIsHeaderVisible(true);
      } else if (extraScrollableSpace > 2 * headerHeight) {
        if (scrollPosition > headerHeight) {
          setIsHeaderVisible(false);
        } else {
          setIsHeaderVisible(true);
        }
      }
    };

    if (screenBody && title)
      screenBody.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      if (screenBody) screenBody.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div>
      <div
        key="navigation-bar"
        className="nav-bar-wrapper"
        style={containerStyle}
      >
        <>
          {showBack && onClose && (
            <div
              data-testid="NavigationBar-closeNavBtn"
              className="nav-bar-close"
              onClick={onClose}
            >
              <BrandIcon
                name={showArrowBack ? 'arrowLeft' : 'closeIcon'}
                style={{ width: 20, height: 20 }}
              />
            </div>
          )}
          {activeStep !== null && isFirstStepsScreen() && (
            <Stepper
              key="stepper"
              activeStep={activeStep}
              numberOfSteps={numberOfValidGroups}
              numberOfCompletedGroups={numberOfCompletedGroups}
              numberOfIncompletedGroups={numberOfIncompletedGroups}
            />
          )}
          {showLogo && !isFirstStepsScreen() && (
            <img src={navLogo} className="nav-logo" />
          )}
          <div className="nav-language">
            {showLanguageSelector && (
              <LanguageSelector white showSelectedLanguage={false} />
            )}
          </div>
          {showHelp && <HelpAction />}
        </>
      </div>
      <div
        ref={headerRef}
        className="nav-bar-header"
        style={{ display: isHeaderVisible ? 'block' : 'none', ...headerStyle }}
      >
        {title && (
          <p
            data-testid={TitleTestId}
            className="nav-bar-title noselect"
            style={titleStyles}
          >
            {title}
          </p>
        )}
        {description && (
          <p
            data-testid={DescriptionTestId}
            className="nav-bar-description noselect"
          >
            {description}
          </p>
        )}
      </div>
    </div>
  );
};

NavigationBar.defaultProps = {
  showHelp: false,
  showArrowBack: false,
  onClose: undefined,
  containerStyle: {},
  customActiveStep: null,
  showLogo: true,
  showBack: true,
  titleStyles: {},
  showLanguageSelector: false,
  headerStyle: {},
};

export default React.memo(NavigationBar);
